import React from 'react';
import { motion } from 'framer-motion';
import { Clock, Calendar, CheckCircle, User, Phone, ArrowRight, Mail, Info } from 'lucide-react';

const OfficeSchedule = () => {
  const weekDays = [
    { day: 'Lunedì', hours: '9:30 - 12:30 | 15:30 - 19:30', open: true },
    { day: 'Martedì', hours: '9:30 - 12:30 | 15:30 - 19:30', open: true },
    { day: 'Mercoledì', hours: '9:30 - 12:30 | 15:30 - 19:30', open: true },
    { day: 'Giovedì', hours: '9:30 - 12:30 | 15:30 - 19:30', open: true },
    { day: 'Venerdì', hours: '9:30 - 12:30 | 15:30 - 19:30', open: true },
    { day: 'Sabato', hours: 'Chiuso', open: false },
    { day: 'Domenica', hours: 'Chiuso', open: false },
  ];
  
  const today = new Date().getDay(); // 0 = Sunday, 1 = Monday, ...
  const todayIndex = today === 0 ? 6 : today - 1; // Adjust to our array index

  return (
    <section className="py-24 bg-gray-900 text-white relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-bl-full"></div>
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/5 rounded-tr-full"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-stretch">
            {/* Left Column - Orari di Apertura */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="flex flex-col h-full"
            >
              <div className="mb-8">
                <span className="text-secondary uppercase tracking-wider font-semibold">A Tua Disposizione</span>
                <h2 className="text-4xl font-monomakh text-white mt-2 mb-6">Orari di Apertura</h2>
                <p className="text-gray-300 mb-6 text-lg leading-relaxed">
                  Siamo a disposizione per consulenze personali durante gli orari di ufficio, 
                  ma se preferisci, è possibile fissare un appuntamento anche al di fuori degli orari standard.
                </p>
              </div>
              
              <div className="bg-gray-800/70 rounded-xl overflow-hidden shadow-xl flex-grow">
                <div className="bg-primary p-4 border-b border-gray-700">
                  <div className="flex items-center">
                    <Clock className="h-5 w-5 text-secondary mr-3" />
                    <h3 className="font-semibold">Orari Settimanali</h3>
                  </div>
                </div>
                
                <div className="p-6">
                  <div className="border-b border-gray-700 pb-4 mb-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <Clock className="h-5 w-5 text-secondary mr-2" />
                        <span className="font-medium">Oggi</span>
                      </div>
                      {weekDays[todayIndex].open ? (
                        <span className="px-3 py-1 bg-green-900/50 text-green-400 rounded-full text-sm font-medium">
                          Aperto
                        </span>
                      ) : (
                        <span className="px-3 py-1 bg-red-900/50 text-red-400 rounded-full text-sm font-medium">
                          Chiuso
                        </span>
                      )}
                    </div>
                    <p className="mt-2 text-gray-300">{weekDays[todayIndex].hours}</p>
                  </div>
                  
                  <ul className="space-y-3">
                    {weekDays.map((day, index) => (
                      <li 
                        key={index} 
                        className={`flex justify-between items-center py-1 ${
                          index === todayIndex ? 'font-semibold text-secondary' : 'text-gray-300'
                        }`}
                      >
                        <span>{day.day}</span>
                        <span>{day.hours}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              
              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <a 
                  href="tel:+393278855115" 
                  className="bg-primary hover:bg-primary-light text-white px-6 py-3 rounded-lg font-semibold flex items-center justify-center transition-colors shadow-lg"
                >
                  <Phone className="h-5 w-5 mr-2" />
                  Chiamaci Ora
                </a>
                <a 
                  href="#appointment" 
                  className="bg-secondary hover:bg-secondary-light text-white px-6 py-3 rounded-lg font-semibold flex items-center justify-center transition-colors shadow-lg"
                >
                  <Calendar className="h-5 w-5 mr-2" />
                  Richiedi Appuntamento
                </a>
              </div>
            </motion.div>
            
            {/* Right Column - Prenota una Visita */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="flex flex-col h-full"
              id="appointment"
            >
              <div className="bg-gray-800/70 rounded-xl overflow-hidden shadow-xl h-full flex flex-col">
                <div className="bg-primary p-4 border-b border-gray-700">
                  <div className="flex items-center">
                    <User className="h-5 w-5 text-secondary mr-3" />
                    <h3 className="font-semibold">Prenota una Visita o Consulenza</h3>
                  </div>
                </div>
                
                <div className="p-6 flex-grow">
                  <p className="text-gray-300 mb-8">
                    Vuoi visitare un immobile specifico o avere una consulenza personalizzata? 
                    Prenota una visita con uno dei nostri agenti nei seguenti modi:
                  </p>
                  
                  <div className="grid md:grid-cols-2 gap-4 mb-6">
                    <div className="bg-gray-700/50 p-5 rounded-lg hover:bg-gray-700/70 transition-colors">
                      <h4 className="text-lg font-semibold text-white mb-4 flex items-center">
                        <Phone className="h-5 w-5 text-secondary mr-2" />
                        Telefonicamente
                      </h4>
                      <p className="text-gray-300 mb-4">
                        Chiamaci direttamente a uno dei nostri numeri per fissare subito un appuntamento.
                      </p>
                      <div className="space-y-2">
                        <p className="text-white">+39 327 8855115 (cell)</p>
                        <p className="text-white">055 752139 (ufficio)</p>
                      </div>
                    </div>
                    
                    <div className="bg-gray-700/50 p-5 rounded-lg hover:bg-gray-700/70 transition-colors">
                      <h4 className="text-lg font-semibold text-white mb-4 flex items-center">
                        <Mail className="h-5 w-5 text-secondary mr-2" />
                        Via Email
                      </h4>
                      <p className="text-gray-300 mb-4">
                        Invia una richiesta specificando l'immobile di interesse e la data preferita.
                      </p>
                      <a href="mailto:info@molinaroimmobiliare.it" className="text-secondary hover:text-secondary-light font-medium">
                        info@molinaroimmobiliare.it
                      </a>
                    </div>
                  </div>
                  
                  <div className="mt-auto bg-gray-700/30 p-6 rounded-lg border-l-4 border-secondary">
                    <div className="flex items-start">
                      <Info className="h-5 w-5 text-secondary mr-3 mt-1 flex-shrink-0" />
                      <div>
                        <h4 className="font-semibold text-white mb-2">Suggerimento</h4>
                        <p className="text-gray-300">
                          Per una visita più efficace, ti consigliamo di fissare un appuntamento in anticipo. 
                          In questo modo possiamo dedicarti il tempo necessario per rispondere a tutte le tue domande.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="mt-8">
                <a 
                  href="/immobili" 
                  className="inline-flex items-center text-secondary hover:text-secondary-light font-medium transition-colors group"
                >
                  Esplora i nostri immobili disponibili
                  <ArrowRight className="ml-1 h-4 w-4 transform transition-transform group-hover:translate-x-1" />
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default OfficeSchedule;